import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import { Alert, Card, Spinner, useToast } from '@platform/ui';

import { OverviewTab } from '../../../features/tenants/overview-tab';
import { useRefreshTenant, useTenantDetail } from '../../../features/tenants/use-tenant';
import { usePlatformClient } from '../../../lib/api/platform-client-context';

export const Route = createFileRoute('/tenants/$tenantId/')({
  component: TenantOverviewPage,
});

/**
 * `/tenants/$id` — the Overview tab (FR-PLAT-07, FR-SUPER-02).
 *
 * The usage figures come from two places that carry the same shape, and the preference
 * between them is deliberate. `GET /platform/reports/usage?tenantId=…` is the report of
 * record and states the range it measured, so it is used when it answers; `TenantDetail.
 * usage` is the snapshot the detail read already carries, so the card renders on the
 * first paint and survives a report the operator's role cannot read. Neither carries an
 * identifier — counts only (API.md §2.13, rules §25).
 */
function TenantOverviewPage() {
  const { tenantId } = Route.useParams();
  const client = usePlatformClient();
  const refresh = useRefreshTenant(tenantId);
  const { toast } = useToast();
  const tenant = useTenantDetail(tenantId);
  const [actionError, setActionError] = useState<string | undefined>(undefined);

  const usage = useQuery({
    queryKey: ['platform-usage', tenantId],
    queryFn: () => client.getUsageReport({ tenantId }),
  });

  const suspend = useMutation({
    mutationFn: (input: { suspended: boolean; reason: string }) =>
      client.setTenantSuspended(tenantId, input),
    onSuccess: async (updated) => {
      setActionError(undefined);
      toast({
        tone: 'success',
        message:
          updated.status === 'suspended'
            ? `${updated.name} is suspended.`
            : `${updated.name} is active again.`,
      });
      await refresh();
    },
    onError: (error: Error) => {
      setActionError(error.message);
    },
  });

  const archive = useMutation({
    mutationFn: (input: { reason: string }) => client.archiveTenant(tenantId, input),
    onSuccess: async (updated) => {
      setActionError(undefined);
      toast({ tone: 'success', message: `${updated.name} is archived.` });
      await refresh();
    },
    onError: (error: Error) => {
      setActionError(error.message);
    },
  });

  if (tenant.isPending) {
    return (
      <Card className="p-6">
        <Spinner label="Loading tenant" />
      </Card>
    );
  }
  if (tenant.isError) {
    return <Alert tone="error">That tenant could not be loaded.</Alert>;
  }

  const reported = usage.data?.tenants.find((row) => row.tenantId === tenantId) ?? null;

  return (
    <OverviewTab
      tenant={tenant.data.tenant}
      usage={reported ?? tenant.data.usage}
      actionError={actionError}
      onSuspend={(reason) => {
        suspend.mutate({ suspended: true, reason });
      }}
      onResume={(reason) => {
        suspend.mutate({ suspended: false, reason });
      }}
      onArchive={(reason) => {
        archive.mutate({ reason });
      }}
    />
  );
}
